import { useState, type CSSProperties } from 'react'
import clsx from 'clsx'

import type { ChatScope, ChatScopeId } from '../../lib/data'
import { BuildBadge } from '../HeaderBand'
import { scopeSizeLine, startedAtClock } from './labels'

// ============================================================================
// THE THREAD HEADER — LifeOS Chat.dc.html, 11a and 11b.
//
// README §10: "Title 600 15px + start time, `Rename` and `Save to journal`
// buttons, and below them a persistent SCOPE row of pills — Everything, Journal,
// School, the specific course, Interests, Learning — with the active one solid
// `ink`. Right-aligned, the scope states its own size. **The scope is never
// hidden in a menu.**"
//
// THE SIZE LINE IS THE HONEST BIT AND IT IS DERIVED. "4 lectures · 1 syllabus ·
// 3 announcements · 5 journal entries in scope" is counted over the corpus by the
// data contract on every read, so it cannot drift from what an answer could
// actually have read. A typed size line is a promise nobody checks.
//
// SCOPE SWITCHING IS REAL — picking a pill re-scopes the thread and the size line
// changes under it. What is NOT real is re-running the question at the new scope:
// that needs the engine, which is V4, and the composer says so.
// ============================================================================

/** Everything, Journal, School, [the course], Interests, Learning — the mockup's order. */
const FIXED_ORDER: ChatScopeId[] = ['everything', 'journal', 'school', 'interests', 'learning']

export function ThreadHeader({
  title,
  startedAt,
  scopes,
  activeScopeId,
  onScope,
}: {
  /** Null on an unsent thread — 11b renders "New thread" in muted with no actions. */
  title: string | null
  startedAt: string | null
  scopes: ChatScope[] | undefined
  activeScopeId: ChatScopeId
  onScope: (id: ChatScopeId) => void
}) {
  const [coursesOpen, setCoursesOpen] = useState(false)

  const all = scopes ?? []
  const courses = all.filter((s) => s.id.startsWith('course:'))
  const active = all.find((s) => s.id === activeScopeId)
  const activeCourse = activeScopeId.startsWith('course:') ? active : undefined

  // The course pill sits between School and Interests when one is chosen; otherwise the row ends
  // with the dashed `a course…` affordance, exactly as 11b draws it.
  const rowIds: ChatScopeId[] = activeCourse
    ? ['everything', 'journal', 'school', activeCourse.id, 'interests', 'learning']
    : FIXED_ORDER

  const sizeLine = active ? scopeSizeLine(active.size) : null

  return (
    <header className="flex flex-none flex-col gap-3 border-b border-line bg-surface px-[30px] pb-[13px] pt-[17px]">
      <div className="flex items-center gap-3">
        <h2
          className={clsx(
            'text-screen font-semibold tracking-[-0.01em]',
            title === null && 'text-muted',
          )}
        >
          {title ?? 'New thread'}
        </h2>
        {title === null ? (
          <span className="text-[11px] leading-none text-microcopy">
            titled automatically after your first message
          </span>
        ) : (
          startedAt && (
            <span className="text-[11px] leading-none text-microcopy">
              started {startedAtClock(startedAt)}
            </span>
          )
        )}

        {title !== null && (
          <div className="ml-auto flex flex-none items-center gap-2">
            <HeaderAction
              label="Rename"
              waitingFor="Renaming a thread arrives with V4, the chat engine — the same version that gives a thread a title in the first place."
            />
            <HeaderAction
              label="Save to journal"
              waitingFor="Saving a conversation into a journal entry arrives with V4. Every answer here already links to the entries it read, so nothing is lost until then."
            />
          </div>
        )}
        {/* No actions row on the unsent thread (11b) — the badge needs its own push right there. */}
        <BuildBadge className={title === null ? 'ml-auto' : undefined} />
      </div>

      <div className="flex items-center gap-2">
        <span className="flex-none text-[9.5px] font-semibold leading-none tracking-[0.08em] text-microcopy">
          SCOPE
        </span>

        {rowIds.map((id) => {
          const scope = all.find((s) => s.id === id)
          if (!scope) return null
          return (
            <ScopeButton
              key={id}
              label={scope.label}
              active={id === activeScopeId}
              onClick={() => onScope(id)}
            />
          )
        })}

        {/* `a course…`, dashed, only while no course is chosen. It is a real control: pressing it
            reveals the four courses rather than opening a menu, because the README's one rule for
            this row is that the scope is never hidden in a menu. */}
        {!activeCourse &&
          (coursesOpen ? (
            courses.map((c) => (
              <ScopeButton
                key={c.id}
                label={c.label}
                active={false}
                onClick={() => {
                  setCoursesOpen(false)
                  onScope(c.id)
                }}
              />
            ))
          ) : (
            <button
              type="button"
              onClick={() => setCoursesOpen(true)}
              style={{ '--mo-rule': 'var(--color-school)' } as CSSProperties}
              className="mo-ink mo-press flex-none rounded-pill border border-dashed border-dash bg-surface px-[11px] py-[5px] text-[11px] font-medium leading-none text-muted hover:text-ink-2"
            >
              a course…
            </button>
          ))}

        {sizeLine && (
          <span className="ml-auto flex-none whitespace-nowrap text-[10.5px] leading-none text-microcopy">
            {sizeLine}
          </span>
        )}
      </div>
    </header>
  )
}

function ScopeButton({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{ '--mo-rule': active ? 'var(--color-surface)' : 'var(--color-ink)' } as CSSProperties}
      className={clsx(
        'mo-ink mo-press flex-none whitespace-nowrap rounded-pill px-[11px] py-[5px] leading-none',
        active
          ? 'bg-ink text-[11px] font-semibold text-surface'
          : 'border border-line bg-surface text-[11px] font-medium text-ink-3',
      )}
    >
      {label}
    </button>
  )
}

/**
 * A header action that is designed and unbuilt. The Connections screen settled the shape: render
 * it fully, keep it reachable by keyboard, carry `aria-disabled`, and name the version that makes
 * it work — never a control that silently does nothing.
 */
function HeaderAction({ label, waitingFor }: { label: string; waitingFor: string }) {
  // Always `aria-disabled` — this component has no `onClick` prop at all, so there is no live
  // state to gate on. §2: "disabled controls have no hover, no cursor change, no transition. Not
  // dimmed-and-still-reactive — inert." Neither the hover fill nor the transition that carried it
  // belongs on a control that will refuse every click.
  return (
    <button
      type="button"
      aria-disabled="true"
      title={waitingFor}
      onClick={(e) => e.preventDefault()}
      className="flex-none cursor-not-allowed whitespace-nowrap rounded-button border border-line bg-surface px-[11px] py-[5px] text-[11px] font-medium leading-none text-ink-3"
    >
      {label}
    </button>
  )
}
